import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  getDocs,
  getFirestore,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";

/**
 * Data access helpers for savedNAISQueries.
 *
 * Schema for each saved query document:
 *   {
 *     userId: string,              // Firebase auth uid (owner)
 *     userDisplayName: string,     // Denormalized — set at save time
 *     userEmail: string,           // Denormalized — set at save time
 *     title: string,               // User-editable, auto-suggested from query
 *     note: string,                // Optional user note
 *     query: string,               // The original question
 *     mode: "insights" | "data_finder",
 *     filters: object | null,      // { theme?, respondent_type?, report_year? }
 *
 *     // Insights mode: answer + sources
 *     answer: string | null,
 *     sources: array | null,       // The hits used by Claude
 *     model: string | null,
 *
 *     // Data Finder mode: just hits
 *     hits: array | null,
 *
 *     shared: boolean,             // visible to other KA admins
 *     saved_at: Timestamp,
 *     updated_at: Timestamp,
 *   }
 */

const COLLECTION = "savedNAISQueries";

function requireAuth() {
  const user = getAuth().currentUser;
  if (!user) throw new Error("Not signed in.");
  return user;
}

function db() {
  return getFirestore();
}

/**
 * Save a new query-answer entry to Firestore.
 * @param {object} payload — see schema above (minus user/timestamp fields, which are added here)
 * @returns {Promise<string>} — the new document ID
 */
export async function saveQuery(payload) {
  const user = requireAuth();
  const docData = {
    userId: user.uid,
    userDisplayName: user.displayName || user.email || "Unknown",
    userEmail: user.email || null,
    title: payload.title || payload.query.slice(0, 80),
    note: payload.note || "",
    query: payload.query,
    mode: payload.mode,
    filters: payload.filters || null,
    answer: payload.answer || null,
    sources: payload.sources || null,
    model: payload.model || null,
    hits: payload.hits || null,
    shared: Boolean(payload.shared),
    saved_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  };
  const docRef = await addDoc(collection(db(), COLLECTION), docData);
  return docRef.id;
}

/**
 * Fetch current user's saved queries, newest first.
 */
export async function listMySavedQueries() {
  const user = requireAuth();
  const q = query(
    collection(db(), COLLECTION),
    where("userId", "==", user.uid),
    orderBy("saved_at", "desc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Fetch shared queries from other users (excludes current user's own shares,
 * which appear in "My Saves").
 *
 * Requires a composite index: shared (ASC) + saved_at (DESC).
 * Firestore will prompt with a creation link on first call if missing.
 */
export async function listSharedQueries() {
  const user = requireAuth();
  const q = query(
    collection(db(), COLLECTION),
    where("shared", "==", true),
    orderBy("saved_at", "desc")
  );
  const snapshot = await getDocs(q);
  // Filter out own shares client-side (can't combine != with other where clauses cleanly in Firestore)
  return snapshot.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((item) => item.userId !== user.uid);
}

/**
 * Update an existing saved query (only allowed for owner — enforced by Firestore rules).
 */
export async function updateSavedQuery(docId, updates) {
  requireAuth();
  const docRef = doc(db(), COLLECTION, docId);
  await updateDoc(docRef, {
    ...updates,
    updated_at: serverTimestamp(),
  });
}

/**
 * Delete a saved query (owner only — enforced by Firestore rules).
 */
export async function deleteSavedQuery(docId) {
  requireAuth();
  const docRef = doc(db(), COLLECTION, docId);
  await deleteDoc(docRef);
}

/**
 * Utility: format a Firestore timestamp for display.
 * Handles serverTimestamp() that hasn't resolved yet (appears as null briefly).
 */
export function formatSavedAt(timestamp) {
  if (!timestamp) return "Just now";
  const date = timestamp instanceof Timestamp ? timestamp.toDate() : timestamp;
  if (!(date instanceof Date) || isNaN(date)) return "—";
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}